import {
  commonFps,
  DEFAULT_RENDER_FPS,
  renderSettingsOptions,
} from "../Render/render.constants";
import {
  DEFAULT_SPEED,
  getSpfForSpeed,
  isValidSpeed,
} from "../Render/scrollSpeedMap";

export const DEFAULT_SCROLL_RESOLUTION = "1280 x 720";
export const DEFAULT_PLAYBACK_FPS = DEFAULT_RENDER_FPS;
export const RENDER_FPS_CHANGED_EVENT = "scrollx-render-fps-changed";
export const RENDER_SETTINGS_CHANGED_EVENT = "scrollx-render-settings-changed";
export const SCROLL_TEXT_PAINT_GUARD_PX = 4;
const SCROLL_PAINT_GUARD_BLOCK_TYPES = new Set([
  "rolename",
  "nameonly",
  "blurb",
  "song",
  "logo",
]);

export const scrollResolutionOptions = Object.keys(renderSettingsOptions);

/**
 * Text glyphs, geometric separators, and scaled logo pixels can paint a few
 * pixels below their final layout box. With a zero user margin, that paint
 * lands outside the block's capture boundary and is clipped when Scroll PNG
 * heights are locked to the preview geometry. Keep this guard in the shared
 * preview layout so preview and render use the exact same block height.
 */
export const getScrollBlockBottomPadding = ({
  blockType,
  marginVertical,
  isCard = false,
}) => {
  const margin = Math.max(0, Number(marginVertical) || 0);
  const normalizedType = String(blockType || "").toLowerCase();
  const needsPaintGuard =
    !isCard &&
    margin === 0 &&
    SCROLL_PAINT_GUARD_BLOCK_TYPES.has(normalizedType);

  return margin + (needsPaintGuard ? SCROLL_TEXT_PAINT_GUARD_PX : 0);
};

/** Footer frame class → render.constants aspectRatio. */
const FRAME_ASPECT_RATIO = {
  Class1x78: 1.78,
  Class1x33: 1.33,
  Class1x85: 1.85,
  Class2x39: 2.39,
  Class1x9: 1.9,
  Class2_35x1: 2,
};

export const DEFAULT_ASPECT_FRAME = "Class1x78";

export const ASPECT_FRAME_OPTIONS = [
  { label: "16:9", value: "Class1x78" },
  { label: "4:3", value: "Class1x33" },
  { label: "1.85:1", value: "Class1x85" },
  { label: "2.39:1", value: "Class2x39" },
];

/** Resolutions whose aspect matches the selected ratio. */
export const resolutionsForAspectFrame = (frameSize) => {
  const ratio = FRAME_ASPECT_RATIO[frameSize];
  if (ratio == null) return scrollResolutionOptions;
  return scrollResolutionOptions.filter(
    (key) => renderSettingsOptions[key]?.aspectRatio === ratio,
  );
};

export const readStoredResolution = () => {
  try {
    const data = JSON.parse(localStorage.getItem("renderSettings"));
    if (data?.resolution && renderSettingsOptions[data.resolution]) {
      return data.resolution;
    }
  } catch (e) {
    // ignore malformed localStorage
  }
  return DEFAULT_SCROLL_RESOLUTION;
};

export const persistStoredResolution = (resolution) => {
  if (!renderSettingsOptions[resolution]) return;
  let current = {};
  try {
    current = JSON.parse(localStorage.getItem("renderSettings")) || {};
  } catch (e) {
    current = {};
  }
  localStorage.setItem(
    "renderSettings",
    JSON.stringify({ ...current, resolution }),
  );
  window.dispatchEvent(
    new CustomEvent(RENDER_SETTINGS_CHANGED_EVENT, {
      detail: { resolution },
    }),
  );
};

const readRenderSettings = () => {
  try {
    return JSON.parse(localStorage.getItem("renderSettings")) || {};
  } catch (e) {
    return {};
  }
};

export const readStoredSpeed = () => {
  const stored = readRenderSettings()?.speed;
  return isValidSpeed(stored) ? Number(stored) : DEFAULT_SPEED;
};

export const persistStoredSpeed = (speed) => {
  const next = Number(speed);
  if (!isValidSpeed(next)) return;
  const current = readRenderSettings();
  localStorage.setItem(
    "renderSettings",
    JSON.stringify({ ...current, speed: next }),
  );
  window.dispatchEvent(
    new CustomEvent(RENDER_SETTINGS_CHANGED_EVENT, {
      detail: { speed: next },
    }),
  );
};

export const readStoredFps = (fallback = DEFAULT_PLAYBACK_FPS) => {
  const stored = Number(readRenderSettings()?.fps);
  if (commonFps.includes(stored)) return stored;

  const fallbackFps = Number(fallback);
  return commonFps.includes(fallbackFps) ? fallbackFps : DEFAULT_PLAYBACK_FPS;
};

export const persistStoredFps = (fps) => {
  const next = Number(fps);
  if (!commonFps.includes(next)) return;

  const current = readRenderSettings();
  localStorage.setItem(
    "renderSettings",
    JSON.stringify({ ...current, fps: next }),
  );
  window.dispatchEvent(
    new CustomEvent(RENDER_FPS_CHANGED_EVENT, { detail: next }),
  );
  window.dispatchEvent(
    new CustomEvent(RENDER_SETTINGS_CHANGED_EVENT, {
      detail: { fps: next },
    }),
  );
};

/**
 * Output px/frame for the speed multiplier, scaled into the preview's
 * canonical layout so on-screen running time matches the rendered video.
 */
export const getPreviewLayoutPpf = (resolutionKey, speed, canonicalWidth) => {
  const key = renderSettingsOptions[resolutionKey]
    ? resolutionKey
    : DEFAULT_SCROLL_RESOLUTION;
  const opts = renderSettingsOptions[key];
  const videoWidth = Math.max(1, Number(opts.width) || 1);
  const layoutWidth = Math.max(1, Number(canonicalWidth) || videoWidth);
  const sppf = getSpfForSpeed({
    resolutionKey: key,
    videoHeight: opts.height,
    speed: isValidSpeed(speed) ? Number(speed) : DEFAULT_SPEED,
  });
  return sppf * (layoutWidth / videoWidth);
};

/**
 * Preview-space height of one output frame: content width scaled by the
 * selected resolution's height / width.
 */
export const getPreviewBlankHeight = (contentWidth, resolutionKey) => {
  const opts =
    renderSettingsOptions[resolutionKey] ||
    renderSettingsOptions[DEFAULT_SCROLL_RESOLUTION];
  const width = Math.max(1, Number(contentWidth) || 1);
  return Math.max(1, Math.round(width * (opts.height / opts.width)));
};

export const formatPreviewTime = (seconds) => {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const total = Math.floor(safe);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const formatPreviewDuration = (seconds) => {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const total = Math.round(safe);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const getPreviewScrollMetrics = (el) => {
  if (!el) {
    return { scrollTop: 0, maxScroll: 0 };
  }
  const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
  return { scrollTop: el.scrollTop, maxScroll };
};

export const getScrollRate = (ppf, playbackFps) =>
  Math.max(0.0001, ppf * playbackFps);
